import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getRef } from '../lib/referral';
import { Phone, Mail, MessageCircle, MapPin, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import FormSuccess from '../components/FormSuccess';
import { HumanCheck } from '../components/HumanCheck';
import { toast } from '../hooks/use-toast';
import { TEAM_AVATARS } from '../components/teamAvatars';
import { useT } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* Rachel's mini-pitch — keyword-based entry-platform recommendation. */
const PLATFORM_INFO = {
  indexai: {
    name: 'Index AI™', href: '/index-ai', price: 99,
    en: 'the fastest way to get AI engines like ChatGPT and Google AI recommending your restaurant.',
    es: 'la forma más rápida de que motores como ChatGPT y Google AI recomienden tu restaurante.',
  },
  workforce: {
    name: 'AI Workforce™', href: '/ai-agents-for-restaurants', price: 199,
    en: 'an AI team that answers calls, replies to reviews and automates your repetitive work 24/7.',
    es: 'un equipo de IA que contesta llamadas, responde reseñas y automatiza tu trabajo repetitivo 24/7.',
  },
  catering: {
    name: 'Catering AI™', href: '/ai/catering-ai', price: 399,
    en: 'the complete platform to launch, grow and scale your catering revenue.',
    es: 'la plataforma completa para lanzar, crecer y escalar tus ingresos de catering.',
  },
};

const KEYWORDS = [
  { id: 'catering', re: /catering|corporate|corporativ|evento|event|banquet/i },
  { id: 'workforce', re: /call|llamad|recepcion|reception|review|rese[nñ]a|autom|staff|personal|equipo|report|whatsapp|respond|answer|agent/i },
  { id: 'indexai', re: /chatgpt|gemini|google|ia |ai |visib|seo|search|b[uú]squ|encuentr|find|discover|recommend|recomien|maps|index/i },
];

function recommendPlatform(text, urlPlatform) {
  if (urlPlatform && PLATFORM_INFO[urlPlatform]) return urlPlatform;
  if (!text || text.trim().length < 12) return null;
  for (const k of KEYWORDS) if (k.re.test(text)) return k.id;
  return 'indexai';
}

const content = {
  en: {
    eyebrow: 'CONTACT',
    title: "Let's talk about your restaurant.",
    body: 'Reach out by phone, email or WhatsApp — or send us a message and a Kitxens specialist will reply within 24 hours.',
    phoneTitle: 'Call us',
    emailTitle: 'Email us',
    whatsappTitle: 'WhatsApp',
    locationTitle: 'Coverage',
    locationValue: 'Helping restaurants worldwide',
    hoursTitle: 'Office hours',
    hoursValue: 'Mon – Fri  ·  9am – 7pm (your local time)',
    formTitle: 'Send a message',
    name: 'Your name',
    email: 'Email address',
    restaurant: 'Restaurant name',
    phone: 'Phone',
    message: 'How can we help?',
    submit: 'Send message',
    sent: 'Message sent',
    sentBody: 'Thanks for reaching out. A Kitxens specialist will respond shortly.',
    again: 'Send another',
    missingTitle: 'Missing fields',
    missingBody: 'Please share your name, email and a quick message.',
    errorTitle: "Couldn't send your message",
    errorBody: 'Please try again in a moment.',
  },
  es: {
    eyebrow: 'CONTACTO',
    title: 'Hablemos de tu restaurante.',
    body: 'Contáctanos por teléfono, email o WhatsApp, o envianos un mensaje y un especialista de Kitxens responderá en menos de 24 horas.',
    phoneTitle: 'Llama',
    emailTitle: 'Email',
    whatsappTitle: 'WhatsApp',
    locationTitle: 'Cobertura',
    locationValue: 'Apoyamos a restaurantes en todo el mundo',
    hoursTitle: 'Horario',
    hoursValue: 'Lun – Vie  ·  9am – 7pm (tu hora local)',
    formTitle: 'Envíanos un mensaje',
    name: 'Tu nombre',
    email: 'Email',
    restaurant: 'Nombre del restaurante',
    phone: 'Teléfono',
    message: '¿Cómo podemos ayudarte?',
    submit: 'Enviar mensaje',
    sent: 'Mensaje enviado',
    sentBody: 'Gracias por escribirnos. Un especialista de Kitxens responderá en breve.',
    again: 'Enviar otro',
    missingTitle: 'Faltan campos',
    missingBody: 'Comparte tu nombre, email y un mensaje breve.',
    errorTitle: 'No pudimos enviar el mensaje',
    errorBody: 'Por favor inténtalo de nuevo en un momento.',
  },
};

export default function ContactPage() {
  const { lang } = useT();
  const c = content[lang] || content.en;
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: '', email: '', restaurant: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [humanToken, setHumanToken] = useState('');
  const nav = useNavigate();

  const rachel = TEAM_AVATARS.find((a) => a.key === 'rachel');
  const recId = useMemo(
    () => recommendPlatform(form.message, searchParams.get('platform')),
    [form.message, searchParams]
  );
  const rec = recId ? PLATFORM_INFO[recId] : null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: c.missingTitle, description: c.missingBody });
      return;
    }
    if (!humanToken) {
      toast({ title: lang === 'es' ? 'Verificación pendiente' : 'Verification pending',
        description: lang === 'es' ? 'Marca "No soy un robot" para enviar.' : 'Check "I\'m not a robot" to send.' });
      return;
    }
    setLoading(true);
    try {
      const [first, ...rest] = form.name.trim().split(' ');
      const r = await axios.post(`${API}/lead`, {
        source: 'contact',
        first_name: first,
        last_name: rest.join(' '),
        email: form.email,
        company: form.restaurant,
        phone: form.phone,
        notes: form.message,
        interest: rec ? `Contact form · Rachel suggested ${rec.name}` : 'Contact form',
        locale: lang,
        source_page: typeof window !== 'undefined' ? window.location.pathname : '/contact',
        metadata: getRef() ? { ref: getRef() } : {},
      }, { headers: { 'X-Human-Token': humanToken } });
      if (form.name) localStorage.setItem('kx_welcome_name', form.name.trim());
      if (form.email) localStorage.setItem('kx_welcome_email', form.email.trim());
      if (form.restaurant) localStorage.setItem('kx_welcome_restaurant', form.restaurant.trim());
      if (r.data?.already_known) {
        toast({ title: lang === 'es' ? 'Ya nos conocemos' : 'We already know each other',
          description: lang === 'es' ? 'Tenemos tus datos guardados — no hace falta repetirlos. Te llevamos a la demo.' : 'We already have your details saved — no need to repeat them. Taking you to the demo.' });
      }
      setDone(true);
      setTimeout(() => nav('/demo'), 2200);
    } catch (err) {
      toast({ title: c.errorTitle, description: c.errorBody });
    } finally {
      setLoading(false);
    }
  };

  const channels = [
    { icon: MessageCircle, title: c.whatsappTitle, value: '+1 (408) 590-1102', sub: '24/7 · ENG | ESP', href: 'https://wa.me/14085901102', external: true },
    { icon: Mail, title: c.emailTitle, value: 'hello@kitxens.com', href: 'mailto:hello@kitxens.com' },
    { icon: Phone, title: c.phoneTitle, value: '+1 (415) 598-2310', href: 'tel:+14155982310' },
  ];

  return (
    <div className="pt-28">
      <SEO
        title={lang === 'es' ? 'Contacto' : 'Contact'}
        description={c.body}
        path="/contact"
      />

      <section className="pt-8 pb-14 md:py-16 lg:py-24 bg-gradient-to-b from-orange-50/40 to-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="text-[#F77F11] text-xs tracking-[0.25em] font-semibold mb-4"
            >
              {c.eyebrow}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl sm:text-5xl lg:text-[52px] font-bold text-neutral-900 leading-[1.05] tracking-[-0.02em]"
            >
              {c.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="mt-5 text-neutral-600 text-base sm:text-lg leading-relaxed max-w-lg"
            >
              {c.body}
            </motion.p>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {channels.map((ch, i) => (
                <motion.a
                  key={ch.title}
                  href={ch.href}
                  target={ch.external ? '_blank' : undefined}
                  rel={ch.external ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.05 * i }}
                  className="group block rounded-2xl border border-neutral-200 bg-white p-5 hover:border-[#F77F11] hover:shadow-lg transition-all"
                >
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mb-3 group-hover:bg-[#F77F11] transition-colors">
                    <ch.icon className="w-5 h-5 text-[#F77F11] group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-xs font-semibold tracking-wide text-neutral-500">{ch.title}</p>
                  <p className="mt-1 font-semibold text-neutral-900">{ch.value}</p>
                  {ch.sub && <p className="mt-0.5 text-[11.5px] font-medium text-neutral-400">{ch.sub}</p>}
                </motion.a>
              ))}
              <div className="rounded-2xl border border-neutral-200 bg-white p-5">
                <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mb-3">
                  <MapPin className="w-5 h-5 text-[#F77F11]" />
                </div>
                <p className="text-xs font-semibold tracking-wide text-neutral-500">{c.locationTitle}</p>
                <p className="mt-1 font-semibold text-neutral-900">{c.locationValue}</p>
                <p className="mt-3 text-xs font-semibold tracking-wide text-neutral-500">{c.hoursTitle}</p>
                <p className="text-sm text-neutral-700">{c.hoursValue}</p>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            id="contact-form"
            className="bg-white rounded-2xl shadow-2xl border border-neutral-100 p-7 sm:p-9 scroll-mt-28"
          >
            {done ? (
              <FormSuccess lang={lang} testId="contact-form-success" />
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <h2 className="text-xl font-bold text-neutral-900 mb-2">{c.formTitle}</h2>
                <div>
                  <Label htmlFor="cn">{c.name}*</Label>
                  <Input id="cn" value={form.name} onChange={(e) => update('name', e.target.value)} className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="ce">{c.email}*</Label>
                    <Input id="ce" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="cp">{c.phone}</Label>
                    <Input id="cp" value={form.phone} onChange={(e) => update('phone', e.target.value)} className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="cr">{c.restaurant}</Label>
                  <Input id="cr" value={form.restaurant} onChange={(e) => update('restaurant', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="cm">{c.message}*</Label>
                  <Textarea id="cm" rows={4} value={form.message} onChange={(e) => update('message', e.target.value)} className="mt-1" />
                </div>
                {rec && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35 }}
                    className="flex items-start gap-3 rounded-2xl border border-orange-100 bg-orange-50/60 p-4"
                    data-testid="rachel-pitch"
                  >
                    <img src={rachel?.img} alt="Rachel — Kitxens" className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#b45309]">
                        {lang === 'es' ? 'Rachel sugiere' : 'Rachel suggests'}
                      </p>
                      <p className="mt-1 text-[13.5px] text-neutral-700 leading-relaxed">
                        {lang === 'es'
                          ? <>Por lo que cuentas, empezaría con <strong>{rec.name}</strong> — {rec.es} {lang === 'es' ? 'Desde' : 'From'} ${rec.price}/mes.</>
                          : <>Based on what you wrote, I'd start with <strong>{rec.name}</strong> — {rec.en} From ${rec.price}/mo.</>}
                      </p>
                      <Link to={rec.href} className="mt-1.5 inline-flex items-center gap-1 text-[13px] font-semibold text-[#F77F11] hover:text-[#d8651a]" data-testid="rachel-pitch-link">
                        {lang === 'es' ? 'Conocer más' : 'Learn more'} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </motion.div>
                )}
                <HumanCheck onToken={setHumanToken} className="mb-1" />
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-form-submit"
                  className="w-full bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 rounded-full text-[15px] font-semibold"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      {c.submit} <ArrowRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
